
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPostBySlug, Post, incrementPostViewCount } from "@/lib/supabase";
import { formatDistanceToNow } from "date-fns";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setIsLoading(true);
      if (slug) {
        const postData = await getPostBySlug(slug);
        setPost(postData);
        
        // Increment view count if post exists
        if (postData?.id) {
          incrementPostViewCount(postData.id);
        }
        
        setIsLoading(false);
      }
    };

    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blog-neon border-t-transparent shadow-[0_0_10px_rgba(67,255,255,0.5)]"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container py-12 text-center glass-card my-8 mx-auto max-w-lg p-8">
        <h1 className="text-3xl font-bold text-gradient">Post not found</h1>
        <p className="mt-4">The post you are looking for does not exist or has been removed.</p>
      </div>
    );
  }

  const formattedDate = post.created_at
    ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
    : "";

  return (
    <div className="container max-w-4xl py-8">
      <article className="prose max-w-none glass-card p-8">
        <h1 className="mb-6 text-4xl font-bold tracking-tight text-gradient">{post.title}</h1>
        
        {/* AdSense Below Title - Futuristic Styled */}
        <div className="mb-6 bg-futuristic-bg-darker p-3 rounded-lg text-center neon-border">
          <div id="adsense-below-title" className="h-[100px] flex items-center justify-center border border-dashed border-futuristic-border">
            <p className="text-blog-neon/50">Ad Space</p>
          </div>
        </div>
        
        <div className="mb-8 text-sm text-muted-foreground">
          Posted {formattedDate}
        </div>
        
        {post.thumbnail && (
          <div className="mb-8 overflow-hidden rounded-lg neon-border animate-pulse-glow">
            <img 
              src={post.thumbnail} 
              alt={post.title} 
              className="w-full object-cover h-[400px]"
            />
          </div>
        )}
        
        <div 
          className="blog-content mt-8"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        
        {/* AdSense Placeholder - Futuristic Styled */}
        <div className="my-8 bg-futuristic-bg-darker p-6 rounded-lg text-center neon-border">
          <p className="text-blog-neon/50">Advertisement</p>
          <div id="adsense-placeholder" className="h-[250px] flex items-center justify-center border border-dashed border-futuristic-border">
            {/* Google AdSense code would go here in production */}
            <p className="text-blog-neon/30">Google AdSense</p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
